import React from 'react';
import type { MessageHistoryItem } from '../../hooks/useMessageHistory';
import { formatDateTime, getSizeLabel, getColorLabel, getBgColorLabel } from 'core/utils/formatUtils';
import { getHospitalNames } from 'core/utils/hospitalUtils'; 
import { Hospital } from '../../hooks/useUsingDashboardHospitals';

interface MessageItemProps {
  message: MessageHistoryItem;
  availableHospitals: Hospital[];
  onDelete: (messageId: string) => void;
}

export const MessageItem = ({ message, availableHospitals, onDelete }: MessageItemProps) => {
  const datetimeNow = new Date();
  const dueDate = new Date(message.due);
  // 表示期限が過ぎている場合は削除ボタンを表示しない
  const isDeletable = datetimeNow < dueDate;

  return (
    <div className="border border-gray-300 rounded-[8px] p-[24px]">
      <div className="flex flex-col gap-[16px]">
        {/* ヘッダー行 */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-[16px]">
            <span className="text-[14px] text-textSecondary noto-sans-jp-medium">
              表示期限
            </span>
            <span className="text-[14px] text-textPrimary noto-sans-jp-regular">
              {formatDateTime(message.due)}
            </span>
          </div>
          <div className="flex gap-[8px]">
            {isDeletable &&<button
              className="px-[16px] py-[8px] bg-gray-400 text-white rounded-[8px] text-[14px] noto-sans-jp-bold hover:bg-gray-500"
              onClick={() => onDelete(message.messageId)}
            >
              削除
            </button>}
          </div>
        </div>

        {/* メッセージ内容 */}
        <div className="border border-gray-200 rounded-[8px] p-[16px] min-h-[80px] bg-gray-50">
          {message.message ? (
            <div
              className={`p-[8px] rounded-[4px] ${
                getSizeLabel(message.size) === '大' ? 'text-[18px]' : 
                getSizeLabel(message.size) === '中' ? 'text-[16px]' : 'text-[14px]'
              } noto-sans-jp-regular`}
              style={{
                color: message.fontColor,
                backgroundColor: message.backgroundColor
              }}
            >
              {message.message}
            </div>
          ) : (
            <div className="text-[14px] text-textSecondary noto-sans-jp-regular">
              メッセージが設定されていません
            </div>
          )}
        </div>

        {/* 詳細情報 */}
        <div className="grid grid-cols-2 gap-x-[32px] gap-y-[8px] text-[12px]">
          <div className="flex gap-[8px]">
            <span className="text-textSecondary noto-sans-jp-medium">文字サイズ:</span>
            <span className="text-textPrimary noto-sans-jp-regular">{getSizeLabel(message.size)}</span>
          </div>
          <div className="flex gap-[8px]">
            <span className="text-textSecondary noto-sans-jp-medium">文字色:</span>
            <span className="text-textPrimary noto-sans-jp-regular">{getColorLabel(message.fontColor)}</span>
          </div>
          <div className="flex gap-[8px]">
            <span className="text-textSecondary noto-sans-jp-medium">背景色:</span>
            <span className="text-textPrimary noto-sans-jp-regular">{getBgColorLabel(message.backgroundColor)}</span>
          </div>
          <div className="flex gap-[8px]">
            <span className="text-textSecondary noto-sans-jp-medium">対象病院:</span>
            <span className="text-textPrimary noto-sans-jp-regular">{getHospitalNames(message.targetHospitalIDs, availableHospitals)}</span>
          </div>
          <div className="flex gap-[8px] col-span-2">
            <span className="text-textSecondary noto-sans-jp-medium">登録日時:</span>
            <span className="text-textPrimary noto-sans-jp-regular">{formatDateTime(message.createdAt)}</span>
          </div>
        </div>
      </div>
    </div>
  );
}; 